<script>
    // Licensed under Gnu Public Licence version 2
    // Copyright (c) 2020-2025 (original work) Open Assessment Technologies SA ;
    import Icon from '../icon/Icon.svelte';

    const skins = {
        primary: 'primary',
        secondary: 'secondary',
        'secondary-thin': 'secondary-thin',
        tertiary: 'tertiary'
    };

    export let max = 1;
    export let value = 0;
    export let valueLabel;
    export let label;
    export let showLabel = true;
    export let skin = skins.primary;
    export let labelPosition = 'center'; // or 'left'

    if (max <= 0) {
        max = 1;
    }

    if (!skins[skin]) {
        skin = skins.primary;
    }
    if (skin === skins.tertiary) {
        labelPosition = 'left';
    }

    const getPercentage = val => `${Math.ceil((val / max) * 100)}%`;
    $: percentage = getPercentage(value);
</script>

<style>
    [role='progressbar'] {
        /* primary */
        --skin-primary-bar-height: 3rem;

        /* secondary */
        --skin-secondary-bar-height: 3rem;

        /* secondary-thin */
        --skin-secondary-thin-bar-height: 1.5rem;

        /* tertiary */
        --skin-tertiary-bar-height: 2rem;
        --label-left-width: 5rem;

        display: block;
        inline-size: 100%;
        text-align: center;
        position: relative;

        /* positioning for label-center */
        & .container,
        & .track,
        & .label {
            top: 0;
            bottom: 0;
            left: 0;
            right: 0;
            position: absolute;
        }
        & :global(svg) {
            inset-inline-start: calc(50% - 1.5rem);
            inset-block-start: calc(50% - 0.75rem);
            position: absolute;
        }
        & .track {
            inline-size: var(--progress-value-width, 0);
            transition: inline-size;
        }
        & .label {
            font-size: var(--fontsize-body-xs);
            line-height: var(--skin-primary-bar-height);
        }

        /* positioning for label-left */
        &.label-left {
            display: flex;
            flex-direction: row-reverse;
            justify-content: space-between;
            gap: 0.5rem;

            & .container {
                position: relative;
                flex: 1 0 auto;
            }
            & .label.label {
                position: relative;
                flex: 0 1 var(--label-left-width);
                word-break: normal;
            }
        }

        /** primary **/
        &.primary {
            block-size: var(--skin-primary-bar-height);
        }
        &.primary .container {
            background: var(--color-bg-inverted);
        }
        &.primary .track {
            background: var(--color-brand);
        }
        &.primary .label {
            color: var(--color-text-inverted);
        }
        &.primary.label-left .label {
            color: var(--color-text-default);
        }

        /** secondary **/
        &.secondary {
            block-size: var(--skin-secondary-bar-height);
        }
        &.secondary .container {
            background: var(--color-bg-default);
            border: var(--border-medium) solid var(--color-border-default);
            border-radius: var(--radius-pill);
        }
        &.secondary .track {
            background: var(--color-bg-inverted);
            border-radius: var(--radius-pill);
        }
        &.secondary .label {
            display: none;
        }

        /** secondary-thin **/
        &.secondary-thin {
            block-size: var(--skin-secondary-thin-bar-height);
        }
        &.secondary-thin .container {
            background: var(--color-bg-default);
            border: var(--border-medium) solid var(--color-border-default);
            border-radius: var(--radius-pill);
        }
        &.secondary-thin .track {
            background: var(--color-bg-inverted);
            border-radius: var(--radius-pill);
        }
        &.secondary-thin .label {
            display: none;
        }

        /** tertiary, label-left **/
        &.tertiary {
            block-size: var(--skin-tertiary-bar-height);

            & .container {
                background: var(--color-gs-light-secondary);
                border: none;
                border-radius: var(--radius-pill);
                clip-path: border-box;
            }
            & .track {
                background: var(--color-bg-inverted);
                border-inline-end: 0.25rem solid var(--color-gs-light);
            }
            & .label {
                color: var(--color-text-inverted);
                line-height: var(--skin-tertiary-bar-height);
            }
            &.maxed :global(svg) {
                color: var(--color-text-inverted);
            }
            &.maxed .track {
                background: var(--color-bg-success);
                border-inline-end: none;
            }
            &.label-left .label {
                color: var(--color-text-default);
            }
            &.label-left.maxed .label {
                color: var(--color-text-success);
            }
        }
    }
    :global(.inverted) {
        & [role='progressbar'] {
            &.tertiary {
                & .container {
                    background: var(--color-gs-dark-secondary);
                }
                & .track {
                    background: var(--color-bg-default);
                    border-color: var(--color-gs-dark);
                }
                & .label {
                    color: var(--color-text-default);
                }
                &.maxed :global(svg) {
                    color: var(--color-text-default);
                }
                &.label-left .label {
                    color: var(--color-text-inverted);
                }
            }
        }
    }
</style>

<div
    role="progressbar"
    class={skin || skins.primary}
    class:label-left={labelPosition === 'left'}
    class:maxed={value === max}
    aria-valuemin={0}
    aria-valuemax={max}
    aria-valuenow={value}
    aria-valuetext={valueLabel || percentage}
>
    <div class="container">
        <div class="track" style="--progress-value-width: {percentage}" />
        {#if skin === skins.tertiary && value === max}
            <Icon name="check-12" ariaHidden />
        {/if}
    </div>
    {#if showLabel}
        <span class="label">{label || valueLabel || percentage}</span>
    {/if}
</div>
